/**
 * Interface for emulator cores
 * Allows switching between different DS emulator implementations
 */

export interface EmulatorCoreMetadata {
  name: string;              // "melonDS", "Desmond", "DeSmuME"
  version: string;           // "0.9.5"
  description: string;       // Human-readable description
  features: {
    cloudSaves: boolean;     // Supports getSaveRAM/setSaveRAM
    customControls: boolean; // Supports setButtonState
    saveStates: boolean;     // Supports save state operations
    fpsDisplay: boolean;     // Exposes FPS data
    touchScreen: boolean;    // Supports touch input
  };
  limitations?: string[];    // Known limitations
}

export interface IEmulatorCore {
  // Metadata
  getMetadata(): EmulatorCoreMetadata;
  // Lifecycle
  initialize(): Promise<void>;
  destroy(): void;
  reset(): void;

  // ROM management
  loadROM(romBuffer: ArrayBuffer): Promise<void>;
  getROMTitle(): string | null;

  // Execution
  runFrame(): void;
  pause(): void;
  resume(): void;

  // Display
  getTopScreen(): Uint8Array | null;
  getBottomScreen(): Uint8Array | null;

  // Audio
  getAudioBuffer(): Float32Array | null;

  // Input
  setButtonState(button: number, pressed: boolean): void;
  setTouchPosition(x: number, y: number): void;
  releaseTouchPosition(): void;

  // Save data
  getSaveRAM(): Uint8Array | null;
  setSaveRAM(saveData: Uint8Array): void;

  // State
  isReady(): boolean;
  getState(): EmulatorState;
  setFPS(fps: number): void;
}

export interface EmulatorState {
  isRunning: boolean;
  isPaused: boolean;
  fps: number;
  romLoaded: boolean;
}

export enum DSButton {
  A = 0,
  B = 1,
  Select = 2,
  Start = 3,
  Right = 4,
  Left = 5,
  Up = 6,
  Down = 7,
  R = 8,
  L = 9,
  X = 10,
  Y = 11,
}
